import { sortByMonthYear } from "./utils";

export const getLineChartOption = (chart1) => {
  const sortedChart1 = sortByMonthYear(chart1);
  const labels = sortedChart1.map((d) => d.MonthYear?.replace("_", " ") || "");
  return {
    animation: false,
    tooltip: {
      confine: true,
      trigger: "axis",
      axisPointer: { type: "line", lineStyle: { color: "#cbd5e1", type: "dashed" } },
      backgroundColor: "rgba(16, 16, 16, 0.92)",
      borderWidth: 0,
      textStyle: { color: "#f8f5f5", fontSize: 13, fontWeight: "bold" },
    },
    legend: {
      top: 0, right: 10, icon: "circle",
      textStyle: { color: "#000", fontSize: 13, fontWeight: "bold" },
    },
    grid: { left: 50, right: 20, top: 45, bottom: 40 },
    xAxis: {
      type: "category", boundaryGap: false, data: labels,
      axisLine: { lineStyle: { color: "#dbe4f5" } },
      axisTick: { show: false },
      axisLabel: { color: "#000", fontSize: 11, fontWeight: "bold", margin: 12 },
      splitLine: { show: true, lineStyle: { color: "#eef2ff" } },
    },
    yAxis: {
      type: "value", name: "Rs Cr",
      nameTextStyle: { color: "#94a3b8", fontSize: 11 },
      axisLine: { show: false }, axisTick: { show: false },
      axisLabel: { color: "#000", fontSize: 10, fontWeight: "bold" },
      splitLine: { lineStyle: { color: "#e6ecfb" } },
    },
    series: [
      {
        name: "Onshore", type: "line", smooth: true, symbol: "circle", symbolSize: 7,
        data: sortedChart1.map((d) => Number(d.OnshoreActual || 0)),
        lineStyle: { width: 3, color: "#6FC2A7" }, itemStyle: { color: "#6FC2A7" },
        areaStyle: { opacity: 0.12 },
      },
      {
        name: "Service", type: "line", smooth: true, symbol: "circle", symbolSize: 7,
        data: sortedChart1.map((d) => Number(d.ServiceActual || 0)),
        lineStyle: { width: 3, color: "#6D5EF7" }, itemStyle: { color: "#6D5EF7" },
      },
      {
        name: "Advance", type: "line", smooth: true, symbol: "circle", symbolSize: 7,
        data: sortedChart1.map((d) => Number(d.AdvanceActual || 0)),
        lineStyle: { width: 3, color: "#3B82F6" }, itemStyle: { color: "#3B82F6" },
      },
    ],
  };
};

export const getBarChartOption = (currentData, previousData, xKey, yKey, color, title) => {
  const EXCLUDE = ["total", "grand total"];
  const filterRows = (rows) =>
    (rows || []).filter((d) => !EXCLUDE.some((ex) => (d[xKey] || "").toLowerCase().includes(ex)));

  const currentRows = filterRows(currentData);
  const currentMonthYear = currentRows[0]?.MonthYear;

  const categoryOrder = [...currentRows]
    .sort((a, b) => Number(b[yKey] || 0) - Number(a[yKey] || 0))
    .map((d) => d[xKey]);

  const currentLookup = {};
  currentRows.forEach((d) => {
    currentLookup[d[xKey]] = Number(d[yKey] || 0);
  });

  const currentLabel = currentMonthYear?.replace("_", " ") || "Current";

  return {
    animation: false,
    tooltip: {
      trigger: "axis", confine: true, axisPointer: { type: "shadow" },
      backgroundColor: "rgba(15,23,42,0.92)", borderWidth: 0,
      textStyle: { color: "#f8fafc", fontSize: 12, fontWeight: "bold" },
      formatter: (params) => {
        const name = params?.[0]?.name || "";
        const lines = params
          .map((p) => `${p.marker} ${p.seriesName}: <b>₹${Number(p.value).toFixed(2)} Cr</b>`)
          .join("<br/>");
        return `${name}<br/>${lines}`;
      },
    },
    legend: { top: 0, right: 10, icon: "circle", textStyle: { color: "#000", fontSize: 12, fontWeight: "bold" } },
    grid: { left: 55, right: 18, top: 45, bottom: 80 },
    xAxis: {
      type: "category", data: categoryOrder,
      axisLine: { lineStyle: { color: "#dbe4f5" } }, axisTick: { show: false },
      axisLabel: { color: "#000", fontSize: 10, fontWeight: "bold", interval: 0, rotate: 35, overflow: "truncate", width: 80 },
      splitLine: { show: false },
    },
    yAxis: {
      type: "value", name: "Rs Cr", nameTextStyle: { color: "#94a3b8", fontSize: 10 },
      axisLine: { show: false }, axisTick: { show: false },
      axisLabel: { color: "#000", fontSize: 10, fontWeight: "bold" },
      splitLine: { lineStyle: { color: "#e6ecfb" } },
    },
    series: [
      {
        name: currentLabel, type: "bar", barWidth: 24,
        data: categoryOrder.map((cat) => currentLookup[cat] ?? 0),
        itemStyle: {
          borderRadius: [6, 6, 0, 0],
          color: {
            type: "linear", x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [{ offset: 0, color }, { offset: 1, color: `${color}CC` }],
          },
        },
      },
    ],
  };
};

export const getDonutChartOption = (chart4) => {
  const filtered = chart4
    .filter((d) => !(d.Name || "").toLowerCase().includes("grand total"))
    .sort((a, b) => Number(b.Values || 0) - Number(a.Values || 0));
  const total = chart4.find((d) => (d.Name || "").toLowerCase().includes("grand total"))?.Values || 0;

  const COLORS = ["#d97706", "#f59e0b", "#0060D0", "#6d4cf5", "#35a57f"];

  return {
    animation: false,
    tooltip: {
      trigger: "item", confine: true,
      formatter: ({ name, value, percent }) => `${name}<br/>₹${Number(value).toFixed(2)} Cr (${percent}%)`,
      backgroundColor: "rgba(15,23,42,0.92)", borderWidth: 0,
      textStyle: { color: "#f8fafc", fontSize: 12, fontWeight: "bold" },
    },
    series: [
      {
        type: "pie", radius: ["65%", "82%"], center: ["50%", "45%"], avoidLabelOverlap: false,
        label: { show: false }, labelLine: { show: false },
        itemStyle: { borderColor: "#fff", borderWidth: 4, borderRadius: 6 },
        data: filtered.map((d, i) => ({
          name: d.Name, value: Number(d.Values || 0), itemStyle: { color: COLORS[i % COLORS.length] },
        })),
      },
    ],
    graphic: [
      { type: "text", left: "center", top: "38%", style: { text: Number(total).toFixed(2), fontSize: 28, fontWeight: 700, fill: "#111827" } },
      { type: "text", left: "center", top: "54%", style: { text: "Total", fontSize: 12, fill: "#94a3b8" } },
    ],
  };
};
